import { CheckCircleFilled, ClockCircleFilled } from "@ant-design/icons";
import { PartnershipTransaction } from "@axl-dev/school-sdk";
import { Tooltip } from "antd";
import { ColumnType } from "antd/es/table";
import { FC, useCallback, useState } from "react";

import { SortingDirection } from "@app/shared/lib/enums/sorting-direction";
import { useSchool, useSdk } from "@app/shared/lib/hooks";
import {
  DateTime,
  HelpText,
  Loc,
  Price,
  Table,
  ValuePlaceholder,
} from "@app/shared/ui";

export const TransactionTable: FC = () => {
  const { partnership } = useSchool();
  const sdk = useSdk();
  const [isEmpty, setEmpty] = useState(false);

  const [isSingleLevel] = useState(partnership?.isSingleLevel ?? true);
  const fetchItemsByFilter = useCallback(
    async (filters: {
      pageTotal: number;
      itemsTotal: number;
      take: number;
      page: number;
      sortName?: string;
      sortType: SortingDirection;
    }) => {
      const res = await sdk.partnership.transactionStore.fetch({
        ...filters,
        sortType: filters.sortType === SortingDirection.asc ? "Asc" : "Desc",
      });

      if (res.body.items.length === 0) {
        setEmpty(true);
      }

      return {
        items: res.body.items,
        filters: {
          ...res.body.filter,
          sortType:
            res.body.filter.sortType === "Desc"
              ? SortingDirection.desc
              : SortingDirection.asc,
          pageTotal: sdk.partnership.transactionStore.filter.pageTotal,
          page: sdk.partnership.transactionStore.filter.page,
        },
      };
    },
    [
      sdk.partnership.transactionStore.filter.page,
      sdk.partnership.transactionStore.filter.pageTotal,
      sdk.partnership.transactionStore,
    ],
  );

  return isEmpty ? (
    <HelpText
      label={
        <Loc
          word="Partnership.transaction.empty"
          default="No transactions available"
        />
      }
      className="fs-14"
    />
  ) : (
    <Table
      defaultFilters={{ take: 25 }}
      fetchItemsByFilter={fetchItemsByFilter}
      rowBordered={false}
      columns={[
        {
          title: <Loc word="Global.date" default="Date" />,
          key: "date",
          width: "10%",
          render: (_text, item) => (
            <DateTime date={item.createdDate} format="L" />
          ),
        },
        {
          title: <Loc word="Global.comment" default="Comment" />,
          key: "number",
          width: "20%",
          render: (_text, item) => (
            <div>
              {item.comment && (
                <div className="line-clamp-2">
                  <Loc word="Global.comment" default="Comment" />: &nbsp;
                  <span className="text-placeholder">{item.comment}</span>
                </div>
              )}
              {item.useBonus && (
                <Loc
                  word="Partnership.transaction.bonusTrasfer"
                  default="Transfer to bonus account"
                  className="text-placeholder"
                />
              )}
              {item.order && (
                <div className="text-placeholder">
                  <Loc word="PurchaseOrder" default="Order" />
                  &nbsp; #{item.order.number}
                </div>
              )}
            </div>
          ),
        },
        ...(!isSingleLevel
          ? [
              {
                title: (
                  <Loc word="Partnership.transaction.level" default="Level" />
                ),
                width: "15%",
                align: "right",
                render: (_text, item) => {
                  if (!item.level) return "-";

                  return (
                    <div>
                      <div>
                        <Loc word="Partnership.transaction.level" />
                        :&nbsp;{item.level}
                      </div>
                      {item.levelPartner && (
                        <div>
                          <ValuePlaceholder
                            value={item.levelPartner.fullName}
                            type="unnamed"
                          />
                        </div>
                      )}
                    </div>
                  );
                },
              } satisfies ColumnType<PartnershipTransaction>,
            ]
          : []),
        {
          title: <Loc word="Partnership.transaction.amount" default="Amount" />,
          key: "amount",
          width: "20%",
          align: "right",
          render: (_text, item) =>
            item.order ? <Price price={item.order.totalAmount} /> : "-",
        },
        {
          title: <Loc word="Partnership.transaction.fee" default="Fee" />,
          key: "paidAmount",
          width: "15%",
          align: "right",
          render: (_text, item) => (
            <Price price={item.fee * (item.type === "deposit" ? 1 : -1)} />
          ),
        },
        {
          title: (
            <Loc
              word="Partnership.transaction.withdrawalLimit"
              default="Available for withdrawal"
            />
          ),
          key: "status",
          width: "20%",
          render: (_text, item) =>
            item.validAfterDate ? (
              <Tooltip
                title={
                  !item.canWithdraw ? (
                    <Loc
                      word="Partnership.transaction.withdrawalValidAfterTemplate"
                      default="Withdrawal will be available {0}"
                    >
                      <DateTime date={item.validAfterDate} format="l" />
                    </Loc>
                  ) : (
                    <Loc
                      word="Partnership.transaction.withdrawalLimit"
                      default="Available for withdrawal"
                    />
                  )
                }
              >
                {item.canWithdraw ? (
                  <span>
                    <CheckCircleFilled className="text-success" />
                    &nbsp;
                    <Price price={item.fee} />
                  </span>
                ) : (
                  <span>
                    <ClockCircleFilled className="text-warning" />
                    &nbsp;
                    <Price price={0} />
                  </span>
                )}
              </Tooltip>
            ) : (
              "-"
            ),
        },
      ]}
      hidePaginationOnePage
      className="partnership-table"
      size="small"
      style={{ maxWidth: 1280 }}
    />
  );
};
